import React, { useMemo, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import Layout from '../components/layout/Layout'
import GlassContainer from '../components/ui/GlassContainer'
import Button from '../components/ui/Button'
import { Database, Globe, Code, Terminal, Copy, Check, GitBranch, BookOpen, Users, FileText, Search, Vote } from 'lucide-react'
import { EXTERNAL_LINKS } from '../utils/constants'

const LandingPage = () => {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText('uvx congressmcp')
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [])

  const features = useMemo(() => [
    { icon: Database, title: '91+ Tools', description: 'Comprehensive coverage of bills, amendments, members, committees, votes, hearings, and more.' },
    { icon: Globe, title: 'All MCP Clients', description: 'Works with Claude Desktop, VS Code, Cursor, and any MCP-compatible AI client.' },
    { icon: Code, title: 'Open Source', description: 'Free forever. Open source with a Sustainable Use License. No API keys from us, no accounts, no rate limits.' },
    { icon: Terminal, title: 'Self-Hostable', description: 'Run locally via stdio or self-host with streamable-http transport.' }
  ], [])

  const toolCategories = useMemo(() => [
    { icon: GitBranch, title: 'Legislation', description: 'Bills, amendments, treaties, summaries — search, details, actions, cosponsors, text' },
    { icon: Users, title: 'Members & Committees', description: 'Search by name/state/party, committee details, sponsored legislation' },
    { icon: Vote, title: 'Voting & Nominations', description: 'Roll call votes, member voting records, nominations, hearings' },
    { icon: FileText, title: 'Records & Hearings', description: 'Congressional record, communications, hearing transcripts' },
    { icon: BookOpen, title: 'Committee Intelligence', description: 'Committee reports, prints, meetings, scheduling data' },
    { icon: Search, title: 'Research & Professional', description: 'CRS reports, congress info, enhanced analytics' }
  ], [])

  const faqs = useMemo(() => [
    { question: 'What is CongressMCP?', answer: 'A free, open-source MCP server that gives AI assistants access to 91+ congressional data tools. It connects Claude Desktop, VS Code, Cursor, and other MCP clients to the Congress.gov API.' },
    { question: 'What is MCP?', answer: 'Model Context Protocol (MCP) is a standard that lets AI assistants connect to external data sources and tools. CongressMCP implements this protocol to provide legislative data.' },
    { question: 'Is it really free?', answer: 'Yes. CongressMCP is free, open-source software under the Sustainable Use License. You just need a free API key from Congress.gov. No accounts with us, no payments, no rate limits from our side.' },
    { question: 'What MCP clients work with it?', answer: 'Any MCP-compatible client works. We provide setup instructions for Claude Desktop, VS Code (with Claude extension), and Cursor. The server uses the standard MCP protocol.' }
  ], [])

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 text-gradient leading-loose pb-4 overflow-visible">
              Congressional Data for AI
            </h1>
            <p className="text-xl lg:text-2xl text-text-secondary mb-8 max-w-3xl mx-auto text-balance">
              Free, open-source MCP server with 91+ tools for legislative research.
              Works with Claude Desktop, VS Code, Cursor, and any MCP client.
            </p>

            <GlassContainer variant="prominent" className="p-6 max-w-lg mx-auto mb-8">
              <div className="flex items-center justify-between bg-primary-800/50 rounded-lg px-4 py-3">
                <code className="text-lg text-text-primary font-mono">$ uvx congressmcp</code>
                <button onClick={handleCopy} className="ml-4 p-2 hover:bg-primary-700/50 rounded transition-colors" aria-label="Copy command">
                  {copied ? <Check size={18} className="text-green-400" /> : <Copy size={18} className="text-text-muted" />}
                </button>
              </div>
            </GlassContainer>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/setup">
                <Button variant="primary" size="lg" className="transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
                  Get Started
                </Button>
              </Link>
              <a href={EXTERNAL_LINKS.GITHUB} target="_blank" rel="noopener noreferrer">
                <Button variant="default" size="lg" className="transform hover:scale-105 transition-all duration-300">
                  View on GitHub
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quickstart */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="heading-xl mb-4">Get Running in 3 Steps</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <GlassContainer variant="card">
              <div className="p-6 text-center">
                <div className="text-3xl font-bold text-accent-blue mb-3">1</div>
                <h3 className="heading-md mb-2">Get API Key</h3>
                <p className="body-sm text-text-muted">
                  Free from{' '}
                  <a href={EXTERNAL_LINKS.CONGRESS_API_SIGNUP} target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">api.congress.gov</a>
                </p>
              </div>
            </GlassContainer>
            <GlassContainer variant="card">
              <div className="p-6 text-center">
                <div className="text-3xl font-bold text-accent-blue mb-3">2</div>
                <h3 className="heading-md mb-2">Install</h3>
                <p className="body-sm text-text-muted">
                  <code className="text-text-primary bg-primary-800/30 px-1 rounded">uvx congressmcp</code>
                </p>
              </div>
            </GlassContainer>
            <GlassContainer variant="card">
              <div className="p-6 text-center">
                <div className="text-3xl font-bold text-accent-blue mb-3">3</div>
                <h3 className="heading-md mb-2">Configure</h3>
                <p className="body-sm text-text-muted">
                  <Link to="/setup" className="text-accent-blue hover:underline">Setup guide</Link>
                </p>
              </div>
            </GlassContainer>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="heading-xl mb-4">Why CongressMCP</h2>
            <p className="body-lg text-text-secondary max-w-2xl mx-auto">
              The most comprehensive open-source MCP server for congressional data.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <GlassContainer key={index} variant="card">
                <div className="p-6 text-center">
                  <feature.icon className="w-12 h-12 text-accent-blue mx-auto mb-4" />
                  <h3 className="heading-md mb-3">{feature.title}</h3>
                  <p className="body-sm text-text-muted leading-relaxed">{feature.description}</p>
                </div>
              </GlassContainer>
            ))}
          </div>
        </div>
      </section>

      {/* Tool Categories */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="heading-xl mb-4">6 Tool Suites</h2>
            <p className="body-lg text-text-secondary max-w-2xl mx-auto">
              91+ operations organized into focused toolsets for every aspect of congressional data.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {toolCategories.map((cat, index) => (
              <GlassContainer key={index} variant="card">
                <div className="p-6">
                  <div className="flex items-center mb-3">
                    <cat.icon className="w-6 h-6 text-accent-blue mr-3" />
                    <h3 className="heading-md">{cat.title}</h3>
                  </div>
                  <p className="body-sm text-text-muted leading-relaxed">{cat.description}</p>
                </div>
              </GlassContainer>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="heading-xl mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="max-w-4xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <GlassContainer key={index} variant="card" className="p-6">
                <h3 className="text-xl font-semibold text-text-primary mb-3">{faq.question}</h3>
                <p className="text-text-secondary leading-relaxed">{faq.answer}</p>
              </GlassContainer>
            ))}
          </div>
        </div>
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
          }))
        })}
        </script>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <GlassContainer variant="subtle" className="text-center p-12">
            <h2 className="heading-xl mb-4">Ready to Get Started?</h2>
            <p className="body-lg text-text-secondary mb-8 max-w-2xl mx-auto">
              Install in under a minute. Free and open source.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/setup">
                <Button variant="primary" size="lg" className="transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
                  Setup Guide
                </Button>
              </Link>
              <a href={EXTERNAL_LINKS.GITHUB} target="_blank" rel="noopener noreferrer">
                <Button variant="default" size="lg" className="transform hover:scale-105 transition-all duration-300">
                  View on GitHub
                </Button>
              </a>
            </div>
          </GlassContainer>
        </div>
      </section>
    </Layout>
  )
}

export default LandingPage
